import * as Api from '@ViewModels';
import { useBaseStepState } from './useBaseStepState';

interface UseTextingStepStateOptions {
	textingStep: Api.ITextingAutomationStep | null;
	template: Api.IAutomationTemplate | null;
	readOnlyProp?: boolean;
}

interface UseTextingStepStateReturn {
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	setSkipStepContactFilter: (filter: Api.IContactsFilterRequest | null | undefined) => void;
	showRequiredFieldError: boolean;
	setShowRequiredFieldError: (show: boolean) => void;
	advancedOptionsToggled: boolean;
	setAdvancedOptionsToggled: (toggled: boolean) => void;
	canEdit: boolean;
	readonly: boolean;
}

/**
 * Custom hook for managing texting step component state.
 * Extends the base step state with texting-specific functionality.
 */
export function useTextingStepState({
	textingStep,
	template,
	readOnlyProp,
}: UseTextingStepStateOptions): UseTextingStepStateReturn {
	const canEdit = template?.status !== 'Archived' && !readOnlyProp;
	const readonly = !canEdit || readOnlyProp;

	// Use base hook for common functionality
	// Note: This is a simplified version that doesn't include save logic
	// The save logic remains in useTextingStepSave for now to maintain compatibility
	const baseState = useBaseStepState({
		step: textingStep,
		template,
		stepId: textingStep?.id || '',
		readonly,
		initialContactFilter: textingStep?.contactFilter,
		hasAdvancedOptions: step => {
			const hasSendOptionSelected =
				!!step.sendFromOptions && step.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
			return !!step.contactFilter?.criteria || hasSendOptionSelected;
		},
	});

	return {
		skipStepContactFilter: baseState.skipStepContactFilter,
		setSkipStepContactFilter: baseState.setSkipStepContactFilter,
		showRequiredFieldError: baseState.showRequiredFieldError,
		setShowRequiredFieldError: baseState.setShowRequiredFieldError,
		advancedOptionsToggled: baseState.advancedOptionsToggled,
		setAdvancedOptionsToggled: baseState.setAdvancedOptionsToggled,
		canEdit,
		readonly,
	};
}
